import React, { useEffect, useState } from "react";
import { FaInfoCircle } from "react-icons/fa";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import Footer from "../../components/footer";
import GetOrgFunctionalChecklistById from "../../hooks/getOrgFunctionalChecklistById";
import { formatUtcDate } from "../../utils/helper";
import {
  getScaleName,
  getScaleValues,
  getScorePillProps,
} from "../../utils/scoringScale";

const ViewChecklist = (props) => {
  const [currentUuid, setCurrentUuid] = useState(0);
  const checklistId = localStorage.getItem("checklist_id");

  // Fetch checklist data

  const { data, isLoading, error } = GetOrgFunctionalChecklistById(checklistId);

  const headings = data && !isLoading && data?.data ? data?.data?.headings : [];

  // Add function to handle overlay positioning
  const handleOverlayPositioning = () => {
    const containers = document.querySelectorAll(".icon-container");
    containers.forEach((container) => {
      const rect = container.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const overlayWidth = 300; // Width of the overlay
      const spaceOnRight = viewportWidth - rect.right;

      // Remove existing positioning classes
      container.classList.remove("position-left", "position-right");

      // Add appropriate positioning class
      if (spaceOnRight < overlayWidth + 20) {
        container.classList.add("position-left");
      } else {
        container.classList.add("position-right");
      }
    });
  };

  // Add useEffect for overlay positioning
  useEffect(() => {
    // Initial positioning with delay to ensure DOM is rendered
    const timer = setTimeout(() => {
      handleOverlayPositioning();
    }, 100);

    // Handle window resize
    const handleResize = () => {
      handleOverlayPositioning();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
    };
  }, [data]); // Re-run when data changes

  return (
    <>
      <div className="container-fluid pt-12 pl-21 pb-12 pr-24 h-100 noScroll">
        <div className="card border-0 h-100">
          <div className="card-body mb-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center">
              <h5 className="font-weight-lighter mb-3 mb-sm-3 mb-md-0 mb-lg-0">
                Admin: View Functional Checklist
              </h5>
              {!isLoading && headings.length > 0 && (
                <div className="media align-items-center">
                  <h6 className="mb-0">
                    <small>Checklist ID: {checklistId}</small>
                  </h6>
                </div>
              )}
            </div>
            <hr />

            {/* Show skeleton while loading */}
            {isLoading && (
              <div className="noScroll h-90 verticalScroll pb-17">
                <div className="p-4">
                  {/* Skeleton for headings */}
                  {[1, 2, 3].map((index) => (
                    <div key={index} className="mb-4">
                      {/* Skeleton for accordion header */}
                      <div
                        className="bg-light rounded p-3 mb-2"
                        style={{ height: "45px" }}
                      >
                        <div
                          className="bg-secondary rounded"
                          style={{
                            height: "16px",
                            width: "60%",
                            opacity: 0.3,
                          }}
                        ></div>
                      </div>
                      {/* Skeleton for accordion content */}
                      <div className="pl-3">
                        {[1, 2, 3, 4].map((subIndex) => (
                          <div
                            key={subIndex}
                            className="row align-items-center my-3"
                          >
                            <div className="col-lg-4 col-md-4 col-sm-4">
                              <div className="bg-light p-3">
                                <div
                                  className="bg-secondary rounded"
                                  style={{
                                    height: "12px",
                                    width: "80%",
                                    opacity: 0.3,
                                  }}
                                ></div>
                              </div>
                            </div>
                            <div className="col-lg-8 col-md-8 col-sm-8">
                              <div className="bg-light p-3">
                                <div
                                  className="bg-secondary rounded"
                                  style={{
                                    height: "12px",
                                    width: "90%",
                                    opacity: 0.3,
                                  }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Show content when loaded */}
            {!isLoading && headings.length > 0 && (
              <div className="noScroll h-90">
                <div className="col-md-4 px-0 pt_1 pb-17">
                  <label
                    htmlFor=""
                    className="small-text border w-358 pl-3 h-35 align-content-center mb-0 rounded-5"
                  >
                    Functional Checklist Title:
                    <span className="ml-3">
                      {data?.data?.name && data?.data?.name}
                    </span>
                  </label>
                </div>
                <div className="noScroll h-90 verticalScroll pb-17">
                  <Accordion
                    allowZeroExpanded
                    allowMultipleExpanded={false}
                    preExpanded={[currentUuid]}
                    className="mb-4 py-0"
                  >
                    {headings.map((heading, i) => (
                      <div key={i}>
                        <div className="chevron" />
                        <AccordionItem
                          className="mb-2"
                          uuid={i}
                          dangerouslySetExpanded={
                            currentUuid === i ? true : false
                          }
                        >
                          <AccordionItemHeading
                            className="accordion__primary"
                            onClick={() => {
                              setCurrentUuid(i === currentUuid ? null : i);
                            }}
                          >
                            <AccordionItemButton
                              style={{
                                height: 45,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <small>{heading.name}</small>
                            </AccordionItemButton>
                          </AccordionItemHeading>
                          <AccordionItemPanel>
                            {/* The scale scores under this heading are
                                recorded on, and the values it offers. */}
                            <div className="d-flex align-items-center flex-wrap my-2 pl-3">
                              <small className="mr-2">
                                Scoring Scale:{" "}
                                <span className="font-weight-bold">
                                  {getScaleName(heading.scoringScale)}
                                </span>
                              </small>
                              <div className="d-flex align-items-center flex-wrap ml-2">
                                {getScaleValues(heading.scoringScale).map(
                                  (value) => {
                                    const pill = getScorePillProps(
                                      heading.scoringScale,
                                      value
                                    );
                                    return (
                                      <span
                                        key={value}
                                        className={`rounded-circle d-flex align-items-center justify-content-center font-weight-bold mr-1 ${pill.className}`}
                                        style={{
                                          width: "24px",
                                          height: "24px",
                                          fontSize: "11px",
                                          ...pill.style,
                                        }}
                                      >
                                        {value}
                                      </span>
                                    );
                                  }
                                )}
                              </div>
                            </div>
                            {heading.subheadings.map((item, key) => (
                              <div key={key} className="row align-items-center">
                                <div className="col-lg-4 col-md-4 col-sm-4 mb-1 mb-sm-0 mb-md-0 md-lg-0">
                                  <div
                                    className={`d-flex justify-content-between ${
                                      key % 2 == 0 ? `bg_light` : ""
                                    } p-3`}
                                  >
                                    <small>{item.name}</small>
                                    <div className="icon-container position-relative">
                                      <small>
                                        <FaInfoCircle size={9} />
                                      </small>
                                      {item.definition && (
                                        <div className="overlay shadow rounded w-236">
                                          <div
                                            className="my-2 pl-2"
                                            style={{
                                              width: "100%",
                                              height: "100%",
                                            }}
                                          >
                                            <p className="small">
                                              {item.definition}
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="col-lg-8 col-md-8 col-sm-8">
                                  <div className="row align-items-center pr-3 px-md-2 d-flex flex-wrap mx-1">
                                    {/* Read-only view - show definition or description */}
                                    <div className="col-12">
                                      <div
                                        className={`p-3 rounded ${
                                          key % 2 == 0 ? `bg_light` : ""
                                        }`}
                                      >
                                        <small className="text-muted">
                                          {item.definition ||
                                            "No definition provided"}
                                        </small>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {headings.length > i + 1 && (
                              <div className="d-flex justify-content-end mt-4 mb-3">
                                <button
                                  className="btn btn-outline-primary w-99 mr-3 rounded"
                                  onClick={() => {
                                    setCurrentUuid(currentUuid + 1);
                                  }}
                                >
                                  <small>Next</small>
                                </button>
                              </div>
                            )}
                          </AccordionItemPanel>
                        </AccordionItem>
                      </div>
                    ))}
                  </Accordion>
                </div>
              </div>
            )}

            {/* Show message when no data */}
            {!isLoading && headings.length === 0 && (
              <div className="noScroll h-90 verticalScroll pb-17">
                <div className="d-flex flex-column justify-content-center align-items-center h-100">
                  <div className="text-center">
                    <h4 className="fs-18 text_primary text-center font-weight-bold mt-4 pt-2 mb-4">
                      No Checklist Data Available
                    </h4>
                    <p className="text-muted fs-13">
                      The checklist could not be loaded or does not exist.
                    </p>
                    <button
                      className="btn btn-outline-primary rounded fs-13"
                      onClick={() => props.history.goBack()}
                    >
                      Go Back
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Show error message */}
            {error && (
              <div className="noScroll h-90 verticalScroll pb-17">
                <div className="d-flex flex-column justify-content-center align-items-center h-100">
                  <div className="text-center">
                    <h4 className="fs-18 text-danger text-center font-weight-bold mt-4 pt-2 mb-4">
                      Error Loading Checklist
                    </h4>
                    <p className="text-muted">
                      There was an error loading the checklist data.
                    </p>
                    <button
                      className="btn btn-outline-primary rounded"
                      onClick={() => window.location.reload()}
                    >
                      Retry
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Created on date - bottom right corner */}
            {!isLoading && data?.data?.dateCreated && (
              <div className="d-flex justify-content-end mt-1">
                <small className="fs-13 font-weight-bold">
                  Created on{" "}
                  {formatUtcDate(data.data.dateCreated)}
                </small>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ViewChecklist;
